import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { daysBetween, type Contact, type LogEntry, type Person, type SchemaOptions, type User } from '@crmsy/shared';
import { todayFor, usePerspective } from '../lib/viewAs.js';
import { db } from '../lib/db.js';
import { applyFilters } from '../lib/filters.js';
import { flattenGroups, groupItems, toggleCollapsed, type GroupKey, type GroupNode } from '../lib/grouping.js';
import { PRIORITY, useKeys } from '../lib/keyboard.js';
import { onContactRenamed } from '../lib/actions.js';
import { useListFilters } from '../lib/useListFilters.jsx';
import { useRecordActions } from '../lib/useRecordActions.jsx';
import { FilterChips } from '../components/FilterChips.jsx';
import { GroupBar, GroupToggle } from '../components/GroupBar.jsx';
import { GroupHeader } from '../components/GroupHeader.jsx';
import { ContactPanel } from '../components/ContactPanel.jsx';
import { Avatar } from '../components/Avatar.jsx';
import { Tag } from '../components/Tag.jsx';
import { useT } from '../i18n/index.js';
import { adminDigestPreview, adminSendDigest, fetchDigestPreview } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { DigestDialog } from './ProfileView.jsx';
import type { DigestPreview } from '@crmsy/shared';

/** §6.2: Overdue · Today · This week · Later unless the URL groups otherwise (D-38). */
const DEFAULT_GROUPS: GroupKey[] = ['due'];

export interface FollowUpsViewProps {
  user: User;
  search: string;
  setSearch: (q: string) => void;
}

/** Follow-ups (§6.2): mine by default, cards grouped Overdue · Today · This week · Later (or by any field, D-38), actions per card. */
export function FollowUpsView({ user, search, setSearch }: FollowUpsViewProps) {
  const t = useT();
  const contacts = useLiveQuery(() => db.contacts.toArray(), [], undefined);
  const people = useLiveQuery(() => db.people.toArray(), [], [] as Person[]);
  const schemaRows = useLiveQuery(() => db.schema.toArray(), [], []);
  const schema = useMemo<SchemaOptions>(() => Object.fromEntries(schemaRows.map((r) => [r.property, r.options])), [schemaRows]);
  const pendingIds = useLiveQuery(async () => new Set((await db.outbox.toArray()).map((o) => o.contactId)), [], new Set<string | null>());
  const log = useLiveQuery(() => db.log.toArray(), [], [] as LogEntry[]);

  const lf = useListFilters({ search, setSearch, contacts, people, schema, defaultMine: true, defaultGroups: DEFAULT_GROUPS, view: 'follow-ups' });
  const { filters, setFilters, groups, setGroups } = lf;
  const me = usePerspective(user);
  const today = todayFor(me);

  // A follow-up is a contact with a next date; by date inside each group.
  const due = useMemo(() => {
    const list = contacts ? applyFilters(contacts, filters, { today, myNotionId: me.notionUserId }).filter((c) => c.nextContactDate) : [];
    return list.sort((a, b) => a.nextContactDate!.localeCompare(b.nextContactDate!));
  }, [contacts, filters, today, me.notionUserId]);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const tree = useMemo(() => groupItems(due, groups, (c: Contact) => c, { schema, people, today }), [due, groups, schema, people, today]);
  const visible = useMemo(() => flattenGroups(tree, due, collapsed).flatMap((r) => (r.kind === 'item' ? [{ c: r.item, path: r.path }] : [])), [tree, due, collapsed]);
  const flat = useMemo(() => visible.map((v) => v.c), [visible]);
  const cardIndex = useMemo(() => new Map(flat.map((c, i) => [c.id, i])), [flat]);

  const latest = useMemo(() => {
    const note = new Map<string, LogEntry>();
    const suggestion = new Map<string, LogEntry>();
    for (const e of log) {
      if (e.archived || !e.contactId) continue;
      const target = e.type === 'suggestion' ? suggestion : e.text && e.type !== 'revert' ? note : null;
      if (!target) continue;
      const prev = target.get(e.contactId);
      if (!prev || prev.at < e.at) target.set(e.contactId, e);
    }
    return { note, suggestion };
  }, [log]);

  const [selected, setSelected] = useState(0);
  const [panelId, setPanelId] = useState<string | null>(null);
  const [digest, setDigest] = useState<DigestPreview | null>(null);
  useEffect(() => onContactRenamed((t, r) => setPanelId((p) => (p === t ? r : p))), []);
  useEffect(() => {
    setSelected((s) => Math.min(s, Math.max(0, flat.length - 1)));
  }, [flat.length]);
  useEffect(() => {
    document.querySelector<HTMLElement>(`[data-card-index="${selected}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const current = flat[selected] ?? null;
  const panelContact = panelId ? (contacts?.find((c) => c.id === panelId) ?? null) : null;
  const ra = useRecordActions(current, { mode: 'list', priority: PRIORITY.view, enabled: !panelContact, schema, people, user });

  useKeys(
    {
      j: () => setSelected((s) => Math.min(s + 1, flat.length - 1)),
      k: () => setSelected((s) => Math.max(s - 1, 0)),
      ArrowDown: () => setSelected((s) => Math.min(s + 1, flat.length - 1)),
      ArrowUp: () => setSelected((s) => Math.max(s - 1, 0)),
      Enter: () => {
        if (current) setPanelId(current.id);
      },
      'Shift+Enter': () => {
        if (current?.url) window.open(current.url, '_blank', 'noopener');
      },
      ...lf.keys,
      h: () => {
        const path = visible[selected]?.path;
        if (path) setCollapsed((c) => toggleCollapsed(c, path));
      },
      Escape: () => {
        if (ra.dialog) return false;
        if (!lf.escape()) return false;
      },
    },
    { priority: PRIORITY.view, enabled: !panelContact },
  );

  const anchorOf = (e: React.MouseEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return { x: r.left, y: r.bottom + 4 };
  };

  if (contacts === undefined) return <div className="p-6 text-sm text-muted">{t('table.opening')}</div>;

  const card = (c: Contact) => {
    const i = cardIndex.get(c.id)!;
    const d = daysBetween(today, c.nextContactDate!);
    const isSel = i === selected;
    const note = latest.note.get(c.id);
    const sug = latest.suggestion.get(c.id);
    return (
      <article
        key={c.id}
        data-card-index={i}
        onClick={() => setSelected(i)}
        onDoubleClick={() => setPanelId(c.id)}
        className={`rounded-xl border bg-paper p-3 text-sm ${isSel ? 'border-accent ring-2 ring-accent-soft' : 'border-line'}`}
      >
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold">
              {pendingIds.has(c.id) && <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-warn-ink align-middle" title={t('panel.saving')} />}
              {c.companyName || t('panel.noCompany')}
            </div>
            <div className="truncate text-muted">{c.contact || '—'}</div>
          </div>
          {c.owner[0] && <Avatar id={c.owner[0]} person={people.find((p) => p.id === c.owner[0])} />}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
          <Tag schema={schema} property="Next Action" value={c.nextAction} extra={c.nextActionExtra.length} />
          <Tag schema={schema} property="Stage" value={c.stage} />
          <span className={d < 0 ? 'font-medium text-danger' : d === 0 ? 'font-medium text-warn-ink' : 'text-muted'}>
            {d < 0 ? t('due.overdue', { n: -d }) : d === 0 ? t('due.today') : t('due.in', { n: d })} · {c.nextContactDate}
          </span>
        </div>
        {note && <p className="mt-2 line-clamp-2 text-xs text-muted">{note.text}</p>}
        {sug && (
          <div className="mt-2 flex items-start gap-2 rounded-lg bg-surface px-2 py-1.5 text-xs">
            <span className="line-clamp-2 flex-1">✨ {sug.text}</span>
            <button type="button" className="shrink-0 text-muted hover:text-accent-dark" onClick={() => void navigator.clipboard?.writeText(sug.text)}>
              {t('message.copy')}
            </button>
          </div>
        )}
        {isSel && (
          <div className="mt-2 flex flex-wrap gap-1">
            <button type="button" className="btn btn-primary" onClick={() => ra.open('followedUp')}>{t('panel.followedUp')}</button>
            <button type="button" className="btn" onClick={() => ra.open('message')}>{t('panel.message')}</button>
            <button type="button" className="btn" onClick={(e) => ra.open('snooze', anchorOf(e))}>{t('panel.snooze')}</button>
            <button type="button" className="btn" onClick={() => ra.open('note')}>{t('panel.note')}</button>
            <button type="button" className="btn" onClick={(e) => ra.open('stage', anchorOf(e))}>{t('card.stage')}</button>
            <button type="button" className="btn" onClick={(e) => ra.open('status', anchorOf(e))}>{t('card.status')}</button>
            <button type="button" className="btn" onClick={(e) => ra.open('nextAction', anchorOf(e))}>{t('card.action')}</button>
            {c.url && <a className="btn" href={c.url} target="_blank" rel="noopener">{t('card.notion')}</a>}
          </div>
        )}
      </article>
    );
  };
  const grid = (items: Contact[]) => <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">{items.map(card)}</div>;
  // Nested sections (D-38): a heading per group, cards at the leaves, collapsed groups show the heading only.
  const sections = (nodes: GroupNode<Contact>[]) =>
    nodes.map((n) => {
      const isCollapsed = collapsed.has(n.path);
      return (
        <section key={n.path} className={n.level === 0 ? 'mb-5' : 'mb-3'} style={{ marginLeft: n.level ? 8 : 0 }}>
          <h2 className="mb-2">
            <GroupHeader node={n} collapsed={isCollapsed} schema={schema} onToggle={() => setCollapsed((c) => toggleCollapsed(c, n.path))} />
          </h2>
          {!isCollapsed && (n.children.length ? sections(n.children) : grid(n.items))}
        </section>
      );
    });

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-paper px-4 py-2">
        <FilterChips filters={filters} setFilters={setFilters} schema={schema} people={people} openPicker={lf.openPicker} />
        <GroupToggle open={lf.barOpen} onToggle={lf.toggleBar} groups={groups} />
        <span className="ml-auto text-xs text-muted">{t('followUps.count', { n: due.length })}</span>
        <button type="button" className="chip" onClick={() => void (me.viewing ? adminDigestPreview(me.userId) : fetchDigestPreview()).then(setDigest).catch((e: unknown) => toast(String(e instanceof Error ? e.message : e), { kind: 'error' }))}>
          {t('followUps.sendDigest')}
        </button>
      </div>
      {lf.barOpen && <GroupBar groups={groups} setGroups={setGroups} />}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {due.length === 0 && (
          <div className="p-6 text-sm text-muted">
            {filters.mine ? t('followUps.nothingMine') : t('followUps.nothing')}{' '}
            {filters.mine && t('followUps.pressI', { key: '\u0000' }).split('\u0000').map((part, i) => (i === 0 ? <span key={i}>{part}<kbd>i</kbd></span> : <span key={i}>{part}</span>))}
          </div>
        )}
        {tree.length ? sections(tree) : grid(due)}
      </div>
      {lf.pickerElement}
      {ra.element}
      {digest && <DigestDialog preview={digest} onClose={() => setDigest(null)} sendAs={me.viewing ? { name: me.name, send: () => adminSendDigest(me.userId) } : undefined} />}
      {panelContact && <ContactPanel contact={panelContact} people={people} schema={schema} user={user} onClose={() => setPanelId(null)} />}
    </div>
  );
}
